import React, { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, X, ChevronRight, Receipt, FileText, Paperclip,
  Download, Trash2, Send, RotateCcw, Circle, Check,
} from "lucide-react";

const STATUS_META = {
  draft:              { label: "Ciornă",                fg: "#6b7280", bg: "#f4f4f5" },
  submitted:          { label: "Trimis",                fg: "#a16207", bg: "#fef9c3" },
  info_requested:     { label: "Info suplimentare",    fg: "#c2410c", bg: "#ffedd5" },
  manager_approved:   { label: "Verificare Finance",   fg: "#1d4ed8", bg: "#dbeafe" },
  finance_verified:   { label: "Verificat",             fg: "#1d4ed8", bg: "#dbeafe" },
  approved_for_payment: { label: "Aprobat pentru plată", fg: "#166534", bg: "#dcfce7" },
  paid:               { label: "Plătit",                fg: "#166534", bg: "#dcfce7" },
  rejected:           { label: "Respins",               fg: "#b91c1c", bg: "#fee2e2" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, fg: "#374151", bg: "#f4f4f5" };
  return (
    <span className="inline-flex items-center px-2.5 h-6 text-[11px] font-medium tracking-tight rounded-md"
      style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

function fmtMoney(v, cur = "RON") {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}


export default function Expenses() {
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ currencies: ["RON"], payment_methods: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null); // report id | "new"
  const [detail, setDetail] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [r, c, p, m] = await Promise.all([
        api.get("/expenses/reports/mine"),
        api.get("/expenses/categories"),
        api.get("/projects"),
        api.get("/expenses/meta"),
      ]);
      setReports(r.data);
      setCategories(c.data.filter(x => x.is_active));
      setProjects(p.data);
      setMeta(m.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = reports;
    if (filter === "draft") list = list.filter(r => r.status === "draft");
    else if (filter === "pending") list = list.filter(r =>
      ["submitted", "info_requested", "manager_approved", "finance_verified"].includes(r.status));
    else if (filter === "paid") list = list.filter(r => r.status === "paid");
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(r => (r.title || "").toLowerCase().includes(s));
    }
    return list;
  }, [reports, q, filter]);

  const stats = useMemo(() => {
    return reports.reduce((acc, r) => {
      acc.total_requested += r.totals?.requested_ron || 0;
      if (r.status === "paid") acc.total_paid += r.totals?.paid_ron || 0;
      if (["submitted", "info_requested", "manager_approved", "finance_verified", "approved_for_payment"].includes(r.status)) {
        acc.pending_count += 1;
      }
      return acc;
    }, { total_requested: 0, total_paid: 0, pending_count: 0 });
  }, [reports]);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1200px] mx-auto">
        <header className="pb-10 pt-2">
          <div className="section-eyebrow">Cheltuieli</div>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <h1 className="text-[42px] md:text-[48px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
              Deconturi
            </h1>
            <Button variant="primary" size="md" icon={Plus}
              onClick={() => setSelected("new")} data-testid="btn-new-report">
              Decont nou
            </Button>
          </div>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Grupează cheltuielile efectuate pentru companie sau un proiect și trimite-le spre aprobare într-un singur decont.
          </p>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <StatCard label="Solicitate anul acesta" value={fmtMoney(stats.total_requested)} />
          <StatCard label="Plătite" value={fmtMoney(stats.total_paid)} muted />
          <StatCard label="În proces" value={String(stats.pending_count)} muted />
        </div>

        {/* Filters */}
        <div className="surface mb-4 px-5 py-3 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[280px] max-w-[400px]">
            <Search className="h-4 w-4 text-ink-muted" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Caută decont după titlu"
              className="flex-1 bg-transparent outline-none border-0 py-2 text-[14px] placeholder:text-ink-muted focus:outline-none" />
            {q && (
              <button onClick={() => setQ("")} className="text-ink-muted hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            {[
              { k: "all", l: "Toate" },
              { k: "draft", l: "Ciorne" },
              { k: "pending", l: "În proces" },
              { k: "paid", l: "Plătite" },
            ].map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)}
                className={`px-3.5 h-8 text-[13px] rounded-[8px] transition-colors ${
                  filter === f.k ? "bg-ink text-white"
                    : "text-ink-muted hover:text-ink hover:bg-neutral-100"
                }`}>
                {f.l}
              </button>
            ))}
          </div>
          <div className="text-[13px] text-ink-muted tabular-nums ml-auto">
            {filtered.length} {filtered.length === 1 ? "decont" : "deconturi"}
          </div>
        </div>

        {/* List */}
        <div className="surface overflow-hidden">
          {loading ? (
            <div className="py-24 flex flex-col items-center gap-3 text-ink-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
              <div className="text-[13px]">Se încarcă…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center">
              <Receipt className="h-8 w-8 mx-auto text-neutral-300 mb-4" />
              <div className="text-[15px] text-ink-muted mb-4">
                {q ? "Niciun rezultat pentru căutarea ta." : "Nu ai deconturi încă."}
              </div>
              {!q && (
                <Button variant="secondary" size="md" icon={Plus}
                  onClick={() => setSelected("new")}>
                  Creează primul decont
                </Button>
              )}
            </div>
          ) : (
            filtered.map((r) => (
              <ReportRow key={r.id} report={r}
                onClick={() => setDetail(r.id)} />
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Sheet */}
      <ReportSheet
        open={!!selected}
        onClose={() => setSelected(null)}
        reportId={selected}
        categories={categories}
        projects={projects}
        meta={meta}
        onSaved={async () => { setSelected(null); await load(); }}
      />

      {/* Detail Sheet (view mode) */}
      <ReportDetailSheet
        open={!!detail}
        onClose={() => setDetail(null)}
        reportId={detail}
        onChanged={async () => { setDetail(null); await load(); }}
        onEdit={(rid) => { setDetail(null); setSelected(rid); }}
      />
    </PortalLayout>
  );
}


function StatCard({ label, value, muted }) {
  return (
    <div className="surface surface-interactive p-6">
      <div className="text-[13px] font-display font-medium text-ink-muted mb-3">
        {label}
      </div>
      <div className={`text-[34px] font-display font-semibold tracking-[-0.02em] tabular-nums leading-none ${
        muted ? "text-ink-muted" : "text-ink"
      }`}>
        {value}
      </div>
    </div>
  );
}


function ReportRow({ report, onClick }) {
  const t = report.totals || {};
  return (
    <button onClick={onClick}
      className="group w-full flex items-center gap-4 py-5 border-b border-neutral-100 hover:bg-neutral-50 transition-colors text-left">
      <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-neutral-100 text-ink-muted shrink-0">
        <Receipt className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="text-[15px] font-medium text-ink truncate">{report.title}</div>
          <StatusPill status={report.status} />
        </div>
        <div className="text-[13px] text-ink-muted truncate">
          {report.items?.length || 0} cheltuieli
          {report.project ? ` · ${report.project.code}` : ""}
          {report.submitted_at ? ` · Trimis ${fmtDate(report.submitted_at)}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[15px] font-medium tabular-nums text-ink">
          {fmtMoney(t.requested_ron)}
        </div>
        {report.status === "paid" && (
          <div className="text-[12px] text-emerald-700 tabular-nums">
            Plătit {fmtMoney(t.paid_ron)}
          </div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-300 group-hover:text-ink shrink-0" />
    </button>
  );
}


// ==========================================================================
// CREATE / EDIT SHEET
// ==========================================================================
function ReportSheet({ open, onClose, reportId, categories, projects, meta, onSaved }) {
  const isNew = reportId === "new";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", project_id: "", items: [],
  });
  const [dirty, setDirty] = useState(false);
  const [existing, setExisting] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const effectiveReportId = savedId || (isNew ? null : reportId);

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      setForm({ title: "", description: "", project_id: "", items: [] });
      setExisting(null);
      setDirty(false);
      setSavedId(null);
    } else if (reportId) {
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/expenses/reports/${reportId}`);
          setExisting(data);
          setForm({
            title: data.title,
            description: data.description || "",
            project_id: data.project_id || "",
            items: (data.items || []).map(it => ({ ...it })),
          });
          setDirty(false);
        } catch (e) { toast.error(apiErr(e)); }
        finally { setLoading(false); }
      })();
    }
  }, [open, reportId, isNew]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dirty]);

  function update(patch) { setForm(f => ({ ...f, ...patch })); setDirty(true); }

  function addItem() {
    const newItem = {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      expense_date: new Date().toISOString().slice(0, 10),
      category_id: categories[0]?.id || "",
      project_id: form.project_id || "",
      phase: "", cost_code: "", vendor: "", description: "",
      amount: 0, vat: 0, currency: "RON", exchange_rate: 1,
      payment_method: "", document_number: "", notes: "",
      mileage_from: "", mileage_to: "", mileage_km: 0,
      mileage_car_plate: "", mileage_purpose: "", mileage_cost_per_km: 0,
      documents: [],
    };
    update({ items: [...form.items, newItem] });
  }

  function updateItem(id, patch) {
    update({ items: form.items.map(it => it.id === id ? { ...it, ...patch } : it) });
  }

  function removeItem(id) {
    update({ items: form.items.filter(it => it.id !== id) });
  }

  function handleClose() {
    if (dirty && !window.confirm("Ai modificări nesalvate. Închizi oricum?")) return;
    if (savedId) { onSaved(); return; }
    onClose();
  }

  async function persistForUpload() {
    // Ensure the report + its items exist server-side so we can attach files,
    // even while creating a brand-new decont.
    const body = {
      title: form.title.trim() || `Decont ${new Date().toISOString().slice(0, 10)}`,
      description: form.description,
      project_id: form.project_id || null,
      items: form.items,
    };
    if (isNew && !savedId) {
      const { data } = await api.post("/expenses/reports", { ...body, submit_now: false });
      setSavedId(data.id);
      setDirty(false);
      return data.id;
    }
    const rid = savedId || reportId;
    await api.put(`/expenses/reports/${rid}`, body);
    setDirty(false);
    return rid;
  }

  async function uploadForItem(itemId, file) {
    try {
      const rid = await persistForUpload();
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/expenses/reports/${rid}/items/${itemId}/attachment`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Update local state
      updateItem(itemId, {
        documents: [...(form.items.find(i => i.id === itemId)?.documents || []), data]
      });
      toast.success("Fișier atașat");
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function save(submit) {
    if (!form.title.trim()) { toast.error("Titlul decontului este obligatoriu"); return; }
    if (submit && form.items.length === 0) { toast.error("Adaugă cel puțin o cheltuială"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        project_id: form.project_id || null,
        items: form.items,
        submit_now: submit,
      };
      const creating = isNew && !savedId;
      if (creating) {
        await api.post("/expenses/reports", payload);
        toast.success(submit ? "Decont trimis spre aprobare" : "Ciornă salvată");
      } else {
        const rid = savedId || reportId;
        // Send update without submit_now
        await api.put(`/expenses/reports/${rid}`, {
          title: payload.title, description: payload.description,
          project_id: payload.project_id, items: payload.items,
        });
        if (submit) {
          await api.post(`/expenses/reports/${rid}/submit`);
          toast.success("Decont trimis spre aprobare");
        } else {
          toast.success("Salvat");
        }
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  }

  const total = form.items.reduce((s, i) => s + (i.amount || 0) * (i.exchange_rate || 1), 0);
  const readOnly = existing && !["draft", "info_requested"].includes(existing.status);

  return (
    <>
      <div onClick={handleClose}
        className={`fixed inset-0 bg-ink/20 transition-opacity duration-300 z-40 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`} />
      <aside className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-[820px] bg-white border-l border-neutral-200 flex flex-col transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-neutral-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-1">
              {isNew ? "Decont nou" : "Editare decont"}
            </div>
            <div className="text-[22px] font-display font-semibold tracking-[-0.01em] text-ink truncate">
              {form.title || "Fără titlu"}
            </div>
          </div>
          <button onClick={handleClose}
            className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-brand mx-auto" />
          ) : (
            <div className="space-y-10">
              {/* Header info */}
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-6">
                  Detalii decont
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="Titlu" required>
                    <UnderlineInput value={form.title}
                      onChange={v => update({ title: v })}
                      placeholder="Ex: Deplasare șantier Brașov" />
                  </Field>
                  <Field label="Proiect principal (opțional)">
                    <UnderlineSelect value={form.project_id || ""}
                      onChange={v => update({ project_id: v })}>
                      <option value="">— fără proiect —</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                      ))}
                    </UnderlineSelect>
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Descriere">
                      <UnderlineInput value={form.description}
                        onChange={v => update({ description: v })}
                        placeholder="Context suplimentar (opțional)" />
                    </Field>
                  </div>
                </div>
              </section>

              {/* Items */}
              <section>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[12px] font-display font-medium text-ink-muted">
                      Cheltuieli
                    </div>
                    <div className="text-[13px] text-ink-muted mt-1">
                      {form.items.length} {form.items.length === 1 ? "linie" : "linii"} · Total {fmtMoney(total)}
                    </div>
                  </div>
                  {!readOnly && (
                    <Button variant="secondary" size="sm" icon={Plus} onClick={addItem}>
                      Adaugă linie
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {form.items.map((it, idx) => (
                    <ItemCard key={it.id} idx={idx + 1} item={it}
                      categories={categories} projects={projects} meta={meta}
                      readOnly={readOnly}
                      onChange={patch => updateItem(it.id, patch)}
                      onRemove={() => removeItem(it.id)}
                      onUpload={file => uploadForItem(it.id, file)}
                      canUpload={!readOnly}
                      reportId={effectiveReportId}
                    />
                  ))}
                  {form.items.length === 0 && (
                    <div className="text-center py-12 border border-dashed border-neutral-200 rounded-xl text-ink-muted">
                      <FileText className="h-6 w-6 mx-auto text-neutral-300 mb-3" />
                      <div className="text-[13px]">Nu ai adăugat cheltuieli.</div>
                      {!readOnly && (
                        <button onClick={addItem} className="mt-3 text-[13px] text-ink underline">
                          Adaugă prima linie
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-neutral-100 flex items-center gap-3">
          {dirty && (
            <span className="text-[12px] text-amber-700 flex items-center gap-1.5">
              <Circle className="h-1.5 w-1.5 fill-amber-500 text-amber-500" />
              Modificări nesalvate
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <Button variant="ghost" size="md" onClick={handleClose}>Renunță</Button>
            {!readOnly && (
              <>
                <Button variant="secondary" size="md" onClick={() => save(false)}
                  loading={saving}>
                  Salvează ciornă
                </Button>
                <Button variant="primary" size="md" icon={Send}
                  onClick={() => save(true)} loading={saving}
                  disabled={form.items.length === 0 || !form.title.trim()}>
                  Trimite spre aprobare
                </Button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}


function ItemCard({ idx, item, categories, projects, meta, readOnly, onChange, onRemove, onUpload, canUpload, reportId }) {
  const cat = categories.find(c => c.id === item.category_id);
  const isMileage = cat?.is_mileage;
  const [expanded, setExpanded] = useState(true);
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="border border-neutral-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-center justify-between bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center gap-3">
          <div className="text-[11px] font-mono text-ink-muted tabular-nums">#{idx}</div>
          {cat && (
            <>
              <span className="text-lg">{cat.icon}</span>
              <span className="text-[14px] font-medium text-ink">{cat.name}</span>
            </>
          )}
          <span className="text-[13px] text-ink-muted tabular-nums">
            {fmtMoney(item.amount, item.currency)}
          </span>
        </div>
        {!readOnly && (
          <button onClick={onRemove}
            className="text-ink-muted hover:text-red-600 h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {expanded && (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4">
            <Field label="Data">
              <UnderlineInput type="date" value={item.expense_date} disabled={readOnly}
                onChange={v => onChange({ expense_date: v })} />
            </Field>
            <Field label="Categorie">
              <UnderlineSelect value={item.category_id} disabled={readOnly}
                onChange={v => onChange({ category_id: v })}>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </UnderlineSelect>
            </Field>
            <Field label="Valoare">
              <UnderlineInput type="number" step="0.01" value={item.amount} disabled={readOnly}
                onChange={v => onChange({ amount: parseFloat(v) || 0 })} />
            </Field>
            <Field label="Monedă">
              <UnderlineSelect value={item.currency} disabled={readOnly}
                onChange={v => onChange({ currency: v })}>
                {meta.currencies.map(c => <option key={c} value={c}>{c}</option>)}
              </UnderlineSelect>
            </Field>

            <Field label="TVA">
              <UnderlineInput type="number" step="0.01" value={item.vat} disabled={readOnly}
                onChange={v => onChange({ vat: parseFloat(v) || 0 })} />
            </Field>
            {item.currency !== "RON" && (
              <Field label="Curs valutar">
                <UnderlineInput type="number" step="0.0001" value={item.exchange_rate} disabled={readOnly}
                  onChange={v => onChange({ exchange_rate: parseFloat(v) || 1 })} />
              </Field>
            )}
            <Field label="Metodă plată">
              <UnderlineSelect value={item.payment_method} disabled={readOnly}
                onChange={v => onChange({ payment_method: v })}>
                <option value="">—</option>
                {meta.payment_methods.map(m => (
                  <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
                ))}
              </UnderlineSelect>
            </Field>
            <Field label="Nr. document">
              <UnderlineInput value={item.document_number} disabled={readOnly}
                onChange={v => onChange({ document_number: v })} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <Field label="Proiect">
              <UnderlineSelect value={item.project_id || ""} disabled={readOnly}
                onChange={v => onChange({ project_id: v || null })}>
                <option value="">— fără —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))}
              </UnderlineSelect>
            </Field>
            <Field label="Fază / activitate">
              <UnderlineInput value={item.phase} disabled={readOnly}
                onChange={v => onChange({ phase: v })} />
            </Field>
            <Field label="Cod de cost">
              <UnderlineInput value={item.cost_code} disabled={readOnly}
                onChange={v => onChange({ cost_code: v })} />
            </Field>
            <Field label="Furnizor">
              <UnderlineInput value={item.vendor} disabled={readOnly}
                onChange={v => onChange({ vendor: v })} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Descriere">
                <UnderlineInput value={item.description} disabled={readOnly}
                  onChange={v => onChange({ description: v })} />
              </Field>
            </div>
          </div>

          {isMileage && (
            <div className="p-4 bg-neutral-50 rounded-lg">
              <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
                Deplasare cu autoturism
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
                <Field label="Punct plecare">
                  <UnderlineInput value={item.mileage_from} disabled={readOnly}
                    onChange={v => onChange({ mileage_from: v })} />
                </Field>
                <Field label="Destinație">
                  <UnderlineInput value={item.mileage_to} disabled={readOnly}
                    onChange={v => onChange({ mileage_to: v })} />
                </Field>
                <Field label="Km">
                  <UnderlineInput type="number" value={item.mileage_km} disabled={readOnly}
                    onChange={v => onChange({ mileage_km: parseFloat(v) || 0 })} />
                </Field>
                <Field label="Nr. mașină">
                  <UnderlineInput value={item.mileage_car_plate} disabled={readOnly}
                    onChange={v => onChange({ mileage_car_plate: v })} />
                </Field>
                <Field label="Cost/km">
                  <UnderlineInput type="number" step="0.01" value={item.mileage_cost_per_km} disabled={readOnly}
                    onChange={v => onChange({ mileage_cost_per_km: parseFloat(v) || 0 })} />
                </Field>
                <Field label="Scop deplasare">
                  <UnderlineInput value={item.mileage_purpose} disabled={readOnly}
                    onChange={v => onChange({ mileage_purpose: v })} />
                </Field>
              </div>
            </div>
          )}

          {/* Attachments */}
          <div>
            <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
              Documente justificative
            </div>
            {(item.documents || []).length > 0 && (
              <div className="space-y-1.5 mb-3">
                {item.documents.map(d => (
                  <a key={d.id}
                    href={`${backend}/api/expenses/reports/${reportId}/items/${item.id}/attachments/${d.id}/download`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center justify-between px-3 py-2 bg-neutral-50 hover:bg-neutral-100 rounded-md text-[13px]">
                    <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{d.filename}</span>
                    <Download className="h-3.5 w-3.5 text-ink-muted" />
                  </a>
                ))}
              </div>
            )}
            {canUpload && (
              <label className="inline-flex items-center gap-2 px-4 h-9 border border-dashed border-neutral-300 rounded-lg text-[13px] text-ink-muted hover:border-ink hover:text-ink cursor-pointer transition-colors">
                <Paperclip className="h-3.5 w-3.5" />
                Adaugă fișier
                <input type="file" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
              </label>
            )}
            {!canUpload && !readOnly && (
              <div className="text-[12px] text-ink-muted italic">
                Salvează ciorna pentru a putea atașa fișiere
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ==========================================================================
// DETAIL SHEET (view mode)
// ==========================================================================
function ReportDetailSheet({ open, onClose, reportId, onChanged, onEdit }) {
  const { user, hasPermission } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!open || !reportId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/expenses/reports/${reportId}`);
        setReport(data);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [open, reportId]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  async function action(url, msg) {
    setBusy(true);
    try {
      await api.post(url);
      toast.success(msg);
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  const t = report?.totals || {};
  const canEdit = report && ["draft", "info_requested"].includes(report.status);
  const canWithdraw = report && ["submitted", "info_requested"].includes(report.status);
  const isOwner = report && user && report.user_id === user.id;
  const canManageExpenses = user?.role === "admin" || hasPermission("finance.manage");
  // Owner poate șterge doar ciornele; admin/finanțe pot șterge orice decont
  const canDelete = report && ((isOwner && report.status === "draft") || canManageExpenses);
  const deleteLabel = report && report.status === "draft" ? "Șterge ciornă" : "Șterge decont";

  return (
    <>
      <div onClick={onClose}
        className={`fixed inset-0 bg-ink/20 transition-opacity duration-300 z-40 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`} />
      <aside className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-[820px] bg-white border-l border-neutral-200 flex flex-col transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>
        {loading || !report ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : (
          <>
            <div className="px-8 pt-8 pb-6 border-b border-neutral-100">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[12px] font-display font-medium text-ink-muted mb-2 flex items-center gap-3">
                    Decont · <StatusPill status={report.status} />
                  </div>
                  <div className="text-[22px] font-display font-semibold tracking-[-0.01em] text-ink truncate">
                    {report.title}
                  </div>
                  {report.description && (
                    <div className="text-[13px] text-ink-muted mt-1">{report.description}</div>
                  )}
                </div>
                <button onClick={onClose}
                  className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mt-6">
                <MiniStat label="Solicitat" value={fmtMoney(t.requested_ron)} />
                <MiniStat label="Aprobat" value={fmtMoney(t.approved_ron)} />
                <MiniStat label="Respins" value={fmtMoney(t.rejected_ron)} />
                <MiniStat label="Plătit" value={fmtMoney(t.paid_ron)}
                  emphasis={report.status === "paid"} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8 space-y-10">
              {/* Items */}
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-4">
                  Cheltuieli ({report.items.length})
                </div>
                <div className="space-y-3">
                  {report.items.map((it) => (
                    <div key={it.id} className="border border-neutral-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-center gap-2.5">
                          <span className="text-lg">{it.category?.icon}</span>
                          <div>
                            <div className="text-[14px] font-medium text-ink">
                              {it.category?.name || "—"}
                            </div>
                            <div className="text-[12px] text-ink-muted">
                              {fmtDate(it.expense_date)} · {it.vendor || "—"}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[15px] font-medium tabular-nums text-ink">
                            {fmtMoney(it.amount, it.currency)}
                          </div>
                          {it.approved_amount !== undefined && it.approved_amount !== it.amount && (
                            <div className="text-[12px] text-emerald-700 tabular-nums">
                              Aprobat: {fmtMoney(it.approved_amount, it.currency)}
                            </div>
                          )}
                        </div>
                      </div>
                      {it.description && (
                        <div className="text-[13px] text-ink-muted mt-2">{it.description}</div>
                      )}
                      {it.project && (
                        <div className="text-[12px] text-ink-muted mt-1">
                          Proiect: {it.project.code}
                          {it.phase && ` · Fază: ${it.phase}`}
                        </div>
                      )}
                      {it.documents?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {it.documents.map(d => (
                            <a key={d.id}
                              href={`${backend}/api/expenses/reports/${report.id}/items/${it.id}/attachments/${d.id}/download`}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-md text-[12px]">
                              <FileText className="h-3 w-3" />{d.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* History */}
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-4">
                  Istoric
                </div>
                <div className="space-y-3">
                  {(report.history || []).map((h, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="text-[11px] text-ink-muted tabular-nums min-w-[120px]">
                        {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                      </div>
                      <div className="flex-1">
                        <div className="text-[13px] text-ink">
                          <span className="font-medium">{h.by_name || "—"}</span> · {actionLabel(h.action)}
                        </div>
                        {h.comment && <div className="text-[12px] text-ink-muted mt-0.5">{h.comment}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="px-8 py-5 border-t border-neutral-100 flex items-center gap-3">
              {canDelete && (
                <Button variant="danger" size="md" data-testid="btn-delete-report"
                  onClick={async () => {
                    if (!window.confirm(`Ștergi definitiv acest decont${report.status !== "draft" ? " (aprobat/în curs)" : ""}?`)) return;
                    try { await api.delete(`/expenses/reports/${report.id}`); toast.success("Șters"); onChanged(); }
                    catch (e) { toast.error(apiErr(e)); }
                  }}>{deleteLabel}</Button>
              )}
              {canWithdraw && (
                <Button variant="secondary" size="md" icon={RotateCcw}
                  onClick={() => action(`/expenses/reports/${report.id}/withdraw`, "Retras")}
                  loading={busy}>Retrage</Button>
              )}
              <div className="ml-auto flex items-center gap-3">
                <Button variant="ghost" size="md" onClick={onClose}>Închide</Button>
                {canEdit && (
                  <Button variant="primary" size="md"
                    onClick={() => onEdit(report.id)}>Editează</Button>
                )}
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function MiniStat({ label, value, emphasis }) {
  return (
    <div>
      <div className="text-[12px] font-display font-medium text-ink-muted mb-1">
        {label}
      </div>
      <div className={`text-[17px] font-display font-semibold tabular-nums ${emphasis ? "text-emerald-700" : "text-ink"}`}>
        {value}
      </div>
    </div>
  );
}


// ==========================================================================
// SHARED UI PRIMITIVES
// ==========================================================================
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-ink-muted mb-2">
        {label}{required && <span className="text-ink"> *</span>}
      </span>
      {children}
    </label>
  );
}

function UnderlineInput({ value, onChange, type = "text", placeholder = "", disabled, step, ...rest }) {
  if (type === "date") {
    return (
      <DateInput value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} disabled={disabled}
        className={`w-full bg-transparent border-0 border-b border-neutral-200 py-2 text-[14px] text-ink placeholder:text-neutral-300 outline-none rounded-none ${disabled ? "text-ink-muted" : "hover:border-neutral-400 focus:border-ink"}`}
        style={{ borderRadius: 0 }} {...rest} />
    );
  }
  return (
    <input type={type} value={value ?? ""} step={step}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder} disabled={disabled}
      className={`w-full bg-transparent border-0 border-b border-neutral-200 py-2 text-[14px] text-ink placeholder:text-neutral-300 outline-none transition-colors rounded-none ${
        disabled ? "text-ink-muted" : "hover:border-neutral-400 focus:border-ink"
      }`}
      style={{ borderRadius: 0 }}
      {...rest} />
  );
}

function UnderlineSelect({ value, onChange, children, disabled }) {
  return (
    <select value={value ?? ""} disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
      className={`w-full appearance-none bg-transparent border-0 border-b border-neutral-200 py-2 pr-6 text-[14px] text-ink outline-none transition-colors cursor-pointer ${
        disabled ? "text-ink-muted" : "hover:border-neutral-400 focus:border-ink"
      }`}
      style={{ borderRadius: 0 }}>
      {children}
    </select>
  );
}


function actionLabel(a) {
  return {
    create: "decont creat",
    submit: "trimis spre aprobare",
    withdraw: "retras",
    manager_approve: "aprobat de manager",
    manager_reject: "respins de manager",
    request_info: "informații suplimentare cerute",
    finance_verify: "verificat de Finance",
    approve_payment: "aprobat pentru plată",
    mark_paid: "marcat plătit",
  }[a] || a;
}
